import React, { useState, useMemo, useEffect } from "react";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

const DataTable = ({
  columns,
  data,
  rowsPerPage = 20,
  searchable = true,
  emptyList,
  onRowClick = () => {},
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPageCount, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");

  // **Handle Sorting**
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  // **Filtered and Sorted Data**
  const filteredData = useMemo(() => {
    let sortedData = [...data];

    // **Search Filtering**
    if (searchable && searchTerm) {
      sortedData = sortedData.filter((row) =>
        columns.some((col) =>
          row[col.accessor]
            ?.toString()
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        )
      );
    }

    // **Sorting**
    if (sortColumn) {
      sortedData.sort((a, b) => {
        const aValue = a[sortColumn] ?? "";
        const bValue = b[sortColumn] ?? "";
        return sortOrder === "asc"
          ? aValue.toString().localeCompare(bValue.toString())
          : bValue.toString().localeCompare(aValue.toString());
      });
    }

    return sortedData;
  }, [data, searchTerm, sortColumn, sortOrder, columns, searchable]);

  // **Pagination Logic**
  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const currentPage = Math.min(totalPages, currentPageCount);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="data-table">
      {/* Search Bar */}
      {searchable && (
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      )}

      {/* Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col.accessor} onClick={() => handleSort(col.accessor)}>
                  {col.label}
                  {sortColumn === col.accessor ? (
                    sortOrder === "asc" ? (
                      <FaSortUp className="sort-icon" />
                    ) : (
                      <FaSortDown className="sort-icon" />
                    )
                  ) : (
                    <FaSort className="sort-icon" />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => (
                <tr
                  key={row._id || rowIndex}
                  onClick={() => onRowClick(row, rowIndex)}
                >
                  {columns.map((col) => (
                    <td key={col.accessor}>{row[col.accessor]}</td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="no-data">
                  {emptyList || "No data available"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          <FaChevronLeft />
        </button>
        <span>
          Page {currentPage} of {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          <FaChevronRight />
        </button>
      </div>
    </div>
  );
};

export default DataTable;
